import React from "react";
import OrientacoesProducaoDisplay from "../../../components/ControleProducao/OrientacoesProducaoDisplay";
import {
  formatQuantidadeComUnd,
  itemNaoUsaTimerNaFicha,
  resolveUndFicha,
} from "../../../utils/controleProducaoQuantidade";
import { publicUploadUrl } from "../../../utils/orientacaoProducaoImageUrl";
import type {
  FichaDiariaItem,
  OrientacaoProducaoData,
  UndMedidaOption,
} from "../../../services/controleProducaoService";
import { agruparItensFichaPorSetor } from "./agruparItensFichaPorSetor";

type LinhaLocal = {
  sobra: string;
  undMedidaIdSobra: number | "";
  producao: string;
  undMedidaIdProducao: number | "";
  naoProduzidoPelaUnidade?: boolean;
  observacaoSobra: string;
  observacaoProducao: string;
};

type FichaPrintMeta = {
  assinadoProducao: boolean;
  assinadoConfere: boolean;
  userProducaoNome: string | null;
  userConfereNome: string | null;
  dataAssinaturaProducao: string | null;
  dataAssinaturaConf: string | null;
  justificativaSemSobra?: string | null;
  ajustadoAdminConferencia?: boolean;
  ajustadoAdminProducao?: boolean;
  imagemAssinaturaConf?: string | null;
  imagemAssinaturaProducao?: string | null;
};

type AssinaturaSetorPrint = {
  setorId: number;
  setorNome: string;
  assinado: boolean;
  userNome?: string | null;
  dataAssinatura?: string | null;
  imagemAssinatura?: string | null;
};

export type FichaDiariaPrintSheetProps = {
  itens: FichaDiariaItem[];
  linhas: Record<number, LinhaLocal>;
  unds: UndMedidaOption[];
  diaSemanaLabel: string;
  dataFormatada: string;
  ficha?: FichaPrintMeta | null;
  nomeLoja: string;
  nomeSetor?: string | null;
  mostrarCronometro?: boolean;
  dataFicha?: string | null;
  modoNoite?: boolean;
  agruparPorSetor?: boolean;
  setoresOrdem?: Array<{ setorId: number; setorNome: string }>;
  assinaturasSetores?: AssinaturaSetorPrint[];
  orientacao?: OrientacaoProducaoData | null;
};

function formatHoraIso(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function CelulaAssinaturaPrint({
  label,
  texto,
  imagemUrl,
}: {
  label: string;
  texto: string | null;
  imagemUrl?: string | null;
}) {
  const src = imagemUrl ? publicUploadUrl(imagemUrl) : "";
  return (
    <div className="ficha-print-footer-cell">
      <div className="ficha-print-footer-label">{label}</div>
      {src ? (
        <img src={src} alt="" className="ficha-print-assinatura" />
      ) : null}
      <div className="ficha-print-footer-value">{texto || "—"}</div>
    </div>
  );
}

const FichaDiariaPrintSheet: React.FC<FichaDiariaPrintSheetProps> = ({
  itens,
  linhas,
  unds,
  diaSemanaLabel,
  dataFormatada,
  ficha,
  nomeLoja,
  nomeSetor,
  mostrarCronometro,
  dataFicha,
  modoNoite,
  agruparPorSetor,
  setoresOrdem,
  assinaturasSetores,
  orientacao,
}) => {
  const formatAssinatura = (
    assinado: boolean,
    nome: string | null | undefined,
    dataIso: string | null | undefined,
    ajustadoPorAdmin?: boolean
  ) => {
    if (!assinado) return null;
    const data = dataIso ? new Date(dataIso).toLocaleString("pt-BR") : "";
    const sufixo = ajustadoPorAdmin ? " (Ajustado por Admin)" : "";
    return `Assinado por: ${nome || "Usuário"} - Unidade: ${nomeLoja}${data ? ` às ${data}` : ""}${sufixo}`;
  };

  const textoProducao = formatAssinatura(
    ficha?.assinadoProducao ?? false,
    ficha?.userProducaoNome,
    ficha?.dataAssinaturaProducao,
    ficha?.ajustadoAdminProducao
  );
  const textoConferenciaBase = formatAssinatura(
    ficha?.assinadoConfere ?? false,
    ficha?.userConfereNome,
    ficha?.dataAssinaturaConf,
    ficha?.ajustadoAdminConferencia
  );
  const textoConferencia =
    textoConferenciaBase && ficha?.justificativaSemSobra?.trim()
      ? `${textoConferenciaBase}. ${ficha.justificativaSemSobra.trim()}`
      : textoConferenciaBase;

  const textosSetores = (assinaturasSetores ?? []).map((setor) => ({
    nome: setor.setorNome,
    texto: formatAssinatura(
      setor.assinado,
      setor.userNome,
      setor.dataAssinatura
    ),
    imagemUrl: setor.assinado ? setor.imagemAssinatura : null,
  }));

  const densityClass =
    itens.length > 24
      ? "ficha-print-root--dense"
      : itens.length > 18
        ? "ficha-print-root--compact"
        : "";

  const blocos = agruparPorSetor
    ? agruparItensFichaPorSetor(itens, setoresOrdem, unds)
    : [{ setorId: null, setorNome: null, itens }];
  const colSpanPrint = 7 + (mostrarCronometro ? 2 : 0);

  return (
    <div className={`ficha-print-root ${densityClass}`.trim()}>
      <header className="ficha-print-top">
        <div className="ficha-print-top-left">
          <img
            src="/dom_belloni_logo.png"
            alt="Dom Belloni"
            className="ficha-print-logo"
          />
          <div className="ficha-print-titles">
            <h1 className="ficha-print-title">
              {modoNoite
                ? nomeLoja
                  ? `FICHA DIÁRIA (NOITE) - ${nomeLoja}`
                  : "FICHA DIÁRIA (NOITE)"
                : nomeLoja
                  ? `LISTA DE PRODUÇÃO - ${nomeLoja}`
                  : "LISTA DE PRODUÇÃO"}
              {!modoNoite && nomeSetor ? ` — ${nomeSetor}` : ""}
            </h1>
            <p className="ficha-print-subtitle">
              {modoNoite ? "Sobras apontadas" : "Solicitação para o dia"}
            </p>
          </div>
        </div>
        <div className="ficha-print-top-right">
          <div className="ficha-print-weekday">{diaSemanaLabel}</div>
          <div className="ficha-print-date">{dataFormatada}</div>
        </div>
      </header>

      <table className="ficha-print-table">
        <thead>
          <tr>
            <th className="ficha-print-th-desc" rowSpan={2}>
              Item
            </th>
            <th className="ficha-print-th-peso" rowSpan={2}>
              Peso
              <br />
              Padrão
            </th>
            <th className="ficha-print-th-qty-group" colSpan={3}>
              Quantidades
            </th>
            {mostrarCronometro && (
              <>
                <th className="ficha-print-th-tempo" rowSpan={2}>
                  Início
                </th>
                <th className="ficha-print-th-tempo" rowSpan={2}>
                  Término
                </th>
              </>
            )}
            <th className="ficha-print-th-obs" rowSpan={2}>
              Obs.
              <br />
              {modoNoite ? "Produção" : "Sobra"}
            </th>
            <th className="ficha-print-th-obs" rowSpan={2}>
              Obs.
              <br />
              {modoNoite ? "Sobra" : "Produção"}
            </th>
          </tr>
          <tr>
            <th className="ficha-print-th-qty-base">BASE</th>
            {modoNoite ? (
              <>
                <th className="ficha-print-th-qty">PRODUÇÃO</th>
                <th className="ficha-print-th-qty">SOBRA</th>
              </>
            ) : (
              <>
                <th className="ficha-print-th-qty">SOBRA</th>
                <th className="ficha-print-th-qty">PRODUÇÃO</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {blocos.flatMap((bloco) => {
            const cabecalho = bloco.setorNome
              ? [
                  <tr key={`setor-${bloco.setorId ?? "sem"}`}>
                    <td className="ficha-print-setor" colSpan={colSpanPrint}>
                      {bloco.setorNome}
                    </td>
                  </tr>,
                ]
              : [];
            const linhasSetor = bloco.itens.map((item) => {
            const linhaKey = item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId;
            const local = linhas[linhaKey];
            const overlay = bloco.setorId != null;
            const producaoTxt = overlay ? item.producao : local?.producao;
            const sobraTxt = overlay
              ? (item.sobra ?? "").trim() || local?.sobra
              : local?.sobra;
            const naoProduziu = overlay
              ? item.naoProduzidoPelaUnidade
              : local?.naoProduzidoPelaUnidade;
            const undProducao = overlay
              ? item.undMedidaIdProducao
              : local?.undMedidaIdProducao;
            const undSobra = overlay
              ? item.undMedidaIdSobra
              : local?.undMedidaIdSobra;
            return (
              <tr key={`${item.itemId}-${item.lojaIdOutra ?? "local"}-${bloco.setorId ?? "x"}`}>
                <td className="ficha-print-td-desc">{item.descricao}</td>
                <td className="ficha-print-td-peso">{item.pesoPadrao}</td>
                <td className="ficha-print-td-qty-base">{item.base || ""}</td>
                {modoNoite ? (
                  <>
                    <td className="ficha-print-td-qty">
                      {naoProduziu
                        ? "Não produzido pela unidade"
                        : formatQuantidadeComUnd(
                            producaoTxt,
                            resolveUndFicha(
                              item.undMedidaIdBase,
                              undProducao
                            ),
                            unds
                          )}
                    </td>
                    <td className="ficha-print-td-qty">
                      {formatQuantidadeComUnd(
                        sobraTxt,
                        resolveUndFicha(
                          item.undMedidaIdBase,
                          undSobra
                        ),
                        unds
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="ficha-print-td-qty">
                      {formatQuantidadeComUnd(
                        sobraTxt,
                        resolveUndFicha(
                          item.undMedidaIdBase,
                          undSobra
                        ),
                        unds
                      )}
                    </td>
                    <td className="ficha-print-td-qty">
                      {naoProduziu
                        ? "Não produzido pela unidade"
                        : formatQuantidadeComUnd(
                            producaoTxt,
                            resolveUndFicha(
                              item.undMedidaIdBase,
                              undProducao
                            ),
                            unds
                          )}
                    </td>
                  </>
                )}
                {mostrarCronometro && (
                  <>
                    <td className="ficha-print-td-tempo">
                      {naoProduziu ||
                      itemNaoUsaTimerNaFicha(item.naoUsaTimer, dataFicha)
                        ? "—"
                        : formatHoraIso(item.cronometragem?.startedAt)}
                    </td>
                    <td className="ficha-print-td-tempo">
                      {naoProduziu ||
                      itemNaoUsaTimerNaFicha(item.naoUsaTimer, dataFicha)
                        ? "—"
                        : formatHoraIso(item.cronometragem?.finishedAt)}
                    </td>
                  </>
                )}
                <td className="ficha-print-td-obs">
                  {modoNoite
                    ? (overlay ? item.observacaoProducao : local?.observacaoProducao) || ""
                    : local?.observacaoSobra || ""}
                </td>
                <td className="ficha-print-td-obs">
                  {modoNoite
                    ? local?.observacaoSobra || ""
                    : (overlay ? item.observacaoProducao : local?.observacaoProducao) || ""}
                </td>
              </tr>
            );
            });
            return [...cabecalho, ...linhasSetor];
          })}
        </tbody>
      </table>

      <div className="ficha-print-lower">
        <OrientacoesProducaoDisplay orientacao={orientacao} variant="print" />

        <footer className="ficha-print-footer">
          <CelulaAssinaturaPrint
            label="Assinatura Conferência"
            texto={textoConferencia}
            imagemUrl={
              ficha?.assinadoConfere ? ficha.imagemAssinaturaConf : null
            }
          />
          {textosSetores.length === 0 && (
            <CelulaAssinaturaPrint
              label="Assinatura Produção"
              texto={textoProducao}
              imagemUrl={
                ficha?.assinadoProducao
                  ? ficha.imagemAssinaturaProducao
                  : null
              }
            />
          )}
          {textosSetores.length > 0 && textoProducao ? (
            <CelulaAssinaturaPrint
              label="Produção total dos setores"
              texto={textoProducao}
              imagemUrl={
                ficha?.assinadoProducao
                  ? ficha.imagemAssinaturaProducao
                  : null
              }
            />
          ) : null}
          {textosSetores.map((setor) => (
            <CelulaAssinaturaPrint
              key={setor.nome}
              label={`Produção — ${setor.nome}`}
              texto={setor.texto}
              imagemUrl={setor.imagemUrl}
            />
          ))}
        </footer>
      </div>
    </div>
  );
};

export default FichaDiariaPrintSheet;
